import { Component, OnInit, inject } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from "@angular/material/dialog";
import { ResourceFactMappingPattern } from "@app/models/rest/resource-fact-mapping-pattern";
import { ResourceFactMappingPatternEntry } from "@app/models/rest/resource-fact-mapping-pattern-entry";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatFormField, MatLabel, MatError } from "@angular/material/form-field";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { DialogHeaderComponent } from "../../../../shared/dialog-header/dialog-header.component";

interface EntryDialogData {
  pattern: ResourceFactMappingPattern;
  entry?: ResourceFactMappingPatternEntry;
}

@Component({
  selector: "app-pattern-entry-dialog",
  templateUrl: "./pattern-entry-dialog.component.html",
  styleUrls: ["./pattern-entry-dialog.component.scss"],
  imports: [
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatError,
    MatDialogActions,
    MatButton,
    DialogHeaderComponent
  ]
})
export class PatternEntryDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<PatternEntryDialogComponent>>(MatDialogRef);
  data = inject<EntryDialogData>(MAT_DIALOG_DATA);

  entryForm: FormGroup;
  isEdit = false;
  availableDays: { value: number; label: string }[] = [];

  ngOnInit(): void {
    // Set up the form
    this.entryForm = this.fb.group({
      offsetDay: [
        0,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(this.data.pattern.length - 1)
        ]
      ],
      startOffsetHour: [
        9,
        [Validators.required, Validators.min(0), Validators.max(23)]
      ],
      startOffsetMinute: [
        0,
        [Validators.required, Validators.min(0), Validators.max(59)]
      ],
      duration: [
        60,
        [Validators.required, Validators.min(5), Validators.max(1440)]
      ],
      durationHours: [
        1,
        [Validators.required, Validators.min(0), Validators.max(24)]
      ],
      durationMinutes: [
        0,
        [Validators.required, Validators.min(0), Validators.max(59)]
      ]
    });

    // Generate available days based on pattern length
    for (let i = 0; i < this.data.pattern.length; i++) {
      this.availableDays.push({
        value: i,
        label: `Day ${i + 1}`
      });
    }

    // If editing an existing entry, populate the form
    if (this.data.entry) {
      this.isEdit = true;
      const { hours, minutes } = this.convertMinutesToHoursAndMinutes(
        this.data.entry.duration
      );
      this.entryForm.patchValue({
        offsetDay: this.data.entry.offsetDay,
        startOffsetHour: this.data.entry.startOffsetHour,
        startOffsetMinute: this.data.entry.startOffsetMinute,
        duration: this.data.entry.duration,
        durationHours: hours,
        durationMinutes: minutes
      });
    } else {
      // Set initial hours and minutes values for a new entry
      this.updateHoursAndMinutesFromDuration(60);
    }

    // Set up value synchronization between duration fields
    this.entryForm.get("durationHours").valueChanges.subscribe(() => {
      this.updateDurationFromHoursAndMinutes();
    });

    this.entryForm.get("durationMinutes").valueChanges.subscribe(() => {
      this.updateDurationFromHoursAndMinutes();
    });

    this.entryForm.get("duration").valueChanges.subscribe((duration) => {
      // Only update if the duration was changed directly, not from the hours/minutes
      if (
        duration &&
        this.convertHoursAndMinutesToMinutes(
          this.entryForm.get("durationHours").value,
          this.entryForm.get("durationMinutes").value
        ) !== duration
      ) {
        this.updateHoursAndMinutesFromDuration(duration);
      }
    });
  }

  // Convert total minutes to hours and minutes components
  convertMinutesToHoursAndMinutes(totalMinutes: number): {
    hours: number;
    minutes: number;
  } {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
  }

  // Convert hours and minutes to total minutes
  convertHoursAndMinutesToMinutes(hours: number, minutes: number): number {
    return hours * 60 + minutes;
  }

  // Update hours and minutes fields when duration changes
  updateHoursAndMinutesFromDuration(duration: number): void {
    const { hours, minutes } = this.convertMinutesToHoursAndMinutes(duration);
    this.entryForm.patchValue(
      {
        durationHours: hours,
        durationMinutes: minutes
      },
      { emitEvent: false }
    );
  }

  // Update duration field when hours or minutes change
  updateDurationFromHoursAndMinutes(): void {
    const hours = this.entryForm.get("durationHours").value || 0;
    const minutes = this.entryForm.get("durationMinutes").value || 0;
    const totalMinutes = this.convertHoursAndMinutesToMinutes(hours, minutes);

    // Don't update if the resulting value is invalid
    if (totalMinutes >= 5 && totalMinutes <= 1440) {
      this.entryForm.patchValue(
        {
          duration: totalMinutes
        },
        { emitEvent: false }
      );
    }
  }

  saveEntry(): void {
    if (!this.entryForm.valid) {
      return;
    }

    // Ensure duration value is set correctly from hours and minutes
    this.updateDurationFromHoursAndMinutes();

    const formValues = this.entryForm.value;

    let entry: ResourceFactMappingPatternEntry;

    if (this.isEdit) {
      // Update existing entry
      entry = this.data.entry;
      entry.offsetDay = formValues.offsetDay;
      entry.startOffsetHour = formValues.startOffsetHour;
      entry.startOffsetMinute = formValues.startOffsetMinute;
      entry.duration = formValues.duration;
    } else {
      // Create new entry
      entry = new ResourceFactMappingPatternEntry();
      entry.offsetDay = formValues.offsetDay;
      entry.startOffsetHour = formValues.startOffsetHour;
      entry.startOffsetMinute = formValues.startOffsetMinute;
      entry.duration = formValues.duration;
      entry.mappingPattern = this.data.pattern;
    }

    this.dialogRef.close({ action: "save", entry });
  }

  deleteEntry(): void {
    if (this.isEdit && this.data.entry) {
      this.dialogRef.close({ action: "delete", entry: this.data.entry });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
